import { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const TIP_W = 320;

export function TourGuide({ steps, open, onClose }) {
  const { t } = useTranslation();
  const [active, setActive] = useState([]);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open) return;
    const visible = (sel) => !sel || Array.from(document.querySelectorAll(sel)).some((el) => el.getBoundingClientRect().width > 0);
    const a = steps.filter((s) => visible(s.selector));
    setActive(a.length ? a : steps);
    setI(0);
  }, [open, steps]);

  const step = active[i];

  useLayoutEffect(() => {
    if (!open || !step) return;
    const compute = () => {
      const el = step.selector ? Array.from(document.querySelectorAll(step.selector)).find((e) => e.getBoundingClientRect().width > 0) : null;
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    compute();
    const id = setTimeout(compute, 260);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => { clearTimeout(id); window.removeEventListener("resize", compute); window.removeEventListener("scroll", compute, true); };
  }, [open, step]);

  if (!open || !step) return null;
  const last = i === active.length - 1;
  const pad = 8;

  let tipTop, tipLeft;
  if (rect) {
    if (rect.left + rect.width + TIP_W + 24 < window.innerWidth) {
      tipLeft = rect.left + rect.width + 16;
      tipTop = Math.max(16, Math.min(rect.top, window.innerHeight - 250));
    } else {
      tipLeft = Math.max(16, Math.min(rect.left, window.innerWidth - TIP_W - 16));
      tipTop = Math.min(rect.top + rect.height + 14, window.innerHeight - 250);
    }
  } else {
    tipTop = window.innerHeight / 2 - 120;
    tipLeft = window.innerWidth / 2 - TIP_W / 2;
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {rect ? (
        <div className="pointer-events-none absolute rounded-xl transition-all duration-300"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
                   boxShadow: "0 0 0 9999px rgba(10,25,47,0.72)", border: "2px solid #06B6D4" }} />
      ) : (
        <div className="absolute inset-0 bg-navy/70" />
      )}
      <div className="absolute rounded-2xl bg-white p-5 premium-shadow" style={{ top: tipTop, left: tipLeft, width: TIP_W }} data-testid="tour-tooltip">
        <button onClick={onClose} className="absolute right-3 top-3 text-slate-400 transition-colors hover:text-navy" data-testid="tour-close" aria-label="Close"><X className="h-4 w-4" /></button>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-cyan-dark">{t("tour.step", { a: i + 1, b: active.length })}</div>
        <h3 className="mt-1 font-heading text-lg font-bold text-navy">{step.title}</h3>
        <p className="mt-2 text-sm text-slate-500">{step.body}</p>
        <div className="mt-5 flex items-center justify-between">
          <button onClick={onClose} className="text-xs text-slate-400 transition-colors hover:text-slate-600 hover:underline" data-testid="tour-skip">{t("tour.skip")}</button>
          <div className="flex gap-2">
            {i > 0 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setI(i - 1)} data-testid="tour-back"><ArrowLeft className="h-4 w-4" /></Button>}
            <Button size="sm" className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => (last ? onClose() : setI(i + 1))} data-testid="tour-next">
              {last ? t("tour.finish") : t("tour.next")}{!last && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
